<template>
  <main class="carte-publique-page">
    <div class="page-shell">
      <section class="state-card" v-if="loading">
        <img src="../assets/logo.png" alt="FIDmarket" />
        <p>Chargement de la carte...</p>
      </section>

      <section class="state-card error" v-else-if="!carte">
        <img src="../assets/logo.png" alt="FIDmarket" />
        <h1>Carte introuvable</h1>
        <p>{{ erreur }}</p>
      </section>

      <section class="state-card activation" v-else-if="carteAActiver">
        <img src="../assets/logo.png" alt="FIDmarket" />
        <h1>Carte disponible à activer</h1>
        <p>Présentez cette carte au commerçant pour la rattacher à votre fiche fidélité.</p>
        <strong>N° {{ numeroCarteAffiche }}</strong>
      </section>

      <template v-else>
        <section class="state-card activation" v-if="carteAActiver">
          <img src="../assets/logo.png" alt="FIDmarket" />
          <p class="hero-kicker">Carte disponible à activer</p>
          <h1>N° {{ numeroCarteAffiche }}</h1>
          <p>Cette carte physique existe bien. Elle sera activée par le commerçant lors de votre passage.</p>
          <strong v-if="carte.commercant">{{ carte.commercant }}</strong>
        </section>

        <template v-else>
        <header class="public-header">
          <div class="brand">
            <img src="../assets/logo.png" alt="FIDmarket" />
            <div>
              <strong>FIDmarket</strong>
              <span>Carte de fidélité digitale</span>
            </div>
          </div>
          <span class="active-badge">
            <span aria-hidden="true"></span>
            Carte active
          </span>
        </header>

        <section class="loyalty-card" aria-label="Carte fidélité digitale">
          <div class="card-glow" aria-hidden="true"></div>

          <div class="card-top">
            <div>
              <span class="card-kicker">Carte digitale</span>
              <h1>{{ carte.prenom || 'Client' }}</h1>
            </div>
            <span class="fid-chip">FID</span>
          </div>

          <p class="merchant-name">{{ carte.commercant || 'Commerçant partenaire' }}</p>

          <div class="card-number-block">
            <span>Numéro de carte</span>
            <strong>{{ numeroCarteAffiche }}</strong>
          </div>

          <div class="card-metrics">
            <article>
              <span>Points disponibles</span>
              <strong>{{ pointsSolde }}</strong>
            </article>
            <article>
              <span>{{ recompenseDisponible ? 'Récompense' : 'Prochain palier' }}</span>
              <strong>{{ rewardLabel }}</strong>
            </article>
          </div>

          <div class="progress-block" v-if="hasRewardRule">
            <div class="progress-track" aria-hidden="true">
              <div class="progress-fill" :style="{ width: progressWidth }"></div>
            </div>
            <p>{{ progressLabel }}</p>
          </div>
        </section>

        <section class="qr-card" aria-label="QR code de la carte">
          <div class="qr-copy">
            <h2>Présentez ce QR code au commerçant</h2>
            <p>Le scan permet de retrouver votre carte fidélité</p>
          </div>

          <div class="qr-frame">
            <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="QR code de la carte fidélité" />
            <span v-else>QR</span>
          </div>
        </section>

        <section class="info-card" aria-label="Informations de la carte">
          <div class="section-title">
            <span>Infos carte</span>
            <strong>Statut actif</strong>
          </div>

          <div class="info-grid">
            <article>
              <span>Carte</span>
              <strong>{{ numeroCarteAffiche }}</strong>
            </article>
            <article>
              <span>Points</span>
              <strong>{{ pointsSolde }}</strong>
            </article>
            <article>
              <span>Statut</span>
              <strong class="status-ok">Actif</strong>
            </article>
          </div>
        </section>

        <section class="history-card" aria-label="Historique fidélité">
          <div class="section-title">
            <span>Historique</span>
            <strong>Derniers mouvements</strong>
          </div>

          <div class="history-list" v-if="historiqueDisponible">
            <article v-for="(item, index) in carte.historique.slice(0, 10)" :key="index" class="history-row">
              <div>
                <strong>{{ historyTitle(item) }}</strong>
                <span>{{ historyDate(item) }}<template v-if="historyDetail(item)"> · {{ historyDetail(item) }}</template></span>
              </div>
              <b :class="{ negative: historySens(item) === 'debit' }">
                {{ historyPoints(item) }}
              </b>
            </article>
          </div>

          <p class="empty-history" v-else>
            Aucun mouvement récent à afficher sur cette carte digitale.
          </p>
        </section>

        <footer class="public-footer">
          FIDmarket — La fidélité qui rapproche
        </footer>
        </template>
      </template>
    </div>
  </main>
</template>

<script>
import api from '../api'
import QRCode from 'qrcode'

export default {
  name: 'CartePubliqueView',
  data() {
    return {
      carte: null,
      loading: true,
      erreur: '',
      currentUrl: '',
      qrCodeUrl: ''
    }
  },
  computed: {
    pointsSolde() {
      return Number(this.carte?.points_solde ?? this.carte?.points ?? 0)
    },
    numeroCarteAffiche() {
      const numero = String(this.carte?.numero_carte || this.carte?.carte?.numero_carte || '').trim()
      return numero ? numero.padStart(6, '0') : '000000'
    },
    recompenseDisponible() {
      return Boolean(this.carte?.recompense_disponible?.disponible)
    },
    hasRewardRule() {
      return Number(this.carte?.recompense_disponible?.seuil_points || 0) > 0
    },
    rewardThreshold() {
      return Number(this.carte?.recompense_disponible?.seuil_points || 0)
    },
    progressWidth() {
      if (!this.hasRewardRule) return '0%'
      return `${Math.min((this.pointsSolde / this.rewardThreshold) * 100, 100)}%`
    },
    progressLabel() {
      if (this.recompenseDisponible) return 'Récompense disponible chez votre commerçant'
      return `${Math.max(this.rewardThreshold - this.pointsSolde, 0)} points avant la prochaine récompense`
    },
    rewardLabel() {
      const recompense = this.carte?.recompense_disponible
      if (!recompense) return 'À venir'
      if (this.recompenseDisponible) {
        const valeur = Number(recompense.valeur_reduction || 0)
        return valeur > 0 ? this.formatEuro(valeur) : 'Disponible'
      }
      return `${Math.max(Number(recompense.seuil_points || 0) - this.pointsSolde, 0)} pts`
    },
    historiqueDisponible() {
      return Array.isArray(this.carte?.historique) && this.carte.historique.length > 0
    },
    carteAActiver() {
      return this.carte?.type === 'carte_a_activer'
    }
  },
  methods: {
    formatEuro(v) {
      return Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
    },
    async genererQrCode() {
      try {
        this.qrCodeUrl = await QRCode.toDataURL(this.currentUrl, {
          width: 280,
          margin: 1,
          color: {
            dark: '#0D1B3D',
            light: '#FFFFFF'
          }
        })
      } catch {
        this.qrCodeUrl = ''
      }
    },
    historyTitle(item) {
      if (item?.libelle) return item.libelle
      if (item?.type === 'utilisation' || item?.type === 'bon_achat') return 'Récompense utilisée'
      return item?.ville ? `Achat à ${item.ville}` : 'Achat fidélité'
    },
    historyDate(item) {
      const date = item?.date || item?.created_at || item?.date_creation
      if (!date) return 'Date non renseignée'
      return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    historyDetail(item) {
      const details = []
      const montant = Number(item?.montant ?? item?.montant_achat ?? 0)
      if (montant > 0) details.push(this.formatEuro(montant))
      if (item?.ville) details.push(item.ville)
      return details.join(' · ')
    },
    historySens(item) {
      if (item?.sens) return item.sens
      return Number(item?.points_utilises || 0) > 0 ? 'debit' : 'credit'
    },
    historyPoints(item) {
      const points = Number(item?.points)
      if (Number.isFinite(points) && points !== 0) {
        return `${points > 0 ? '+' : ''}${points} pts`
      }
      const utilises = Number(item?.points_utilises || 0)
      if (utilises > 0) return `-${utilises} pts`
      const gagnes = Number(item?.points_gagnes || 0)
      return `+${gagnes} pts`
    },
    async chargerCarte() {
      this.loading = true
      this.erreur = ''
      try {
        const token = this.$route.params.token
        const res = await api.get(`/public/carte/${encodeURIComponent(token)}`)
        this.carte = res.data
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Impossible de charger cette carte.'
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.currentUrl = window.location.href
    this.genererQrCode()
    this.chargerCarte()
  }
}
</script>

<style scoped>
.carte-publique-page {
  min-height: 100vh;
  padding: 24px 14px 30px;
  background:
    radial-gradient(circle at top left, rgba(30, 111, 220, 0.12), transparent 30rem),
    linear-gradient(180deg, #F8FAFC 0%, #EEF4FB 100%);
  color: #0F172A;
  font-family: 'Poppins', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.page-shell {
  width: min(100%, 430px);
  margin: 0 auto;
}

.public-header,
.brand,
.active-badge,
.card-top,
.card-metrics,
.section-title,
.history-row {
  display: flex;
  align-items: center;
}

.public-header {
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 18px;
}

.brand {
  min-width: 0;
  gap: 11px;
}

.brand img,
.state-card img {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border-radius: 14px;
  background: #FFFFFF;
  box-shadow: 0 12px 28px rgba(13, 27, 61, 0.12);
}

.brand strong {
  display: block;
  color: #0D1B3D;
  font-size: 17px;
  line-height: 1.1;
  font-weight: 800;
}

.brand span {
  display: block;
  margin-top: 3px;
  color: #64748B;
  font-size: 12px;
  font-weight: 600;
}

.active-badge {
  flex: 0 0 auto;
  gap: 7px;
  border: 1px solid rgba(34, 197, 94, 0.28);
  border-radius: 999px;
  padding: 8px 11px;
  background: rgba(34, 197, 94, 0.1);
  color: #15803D;
  font-size: 12px;
  font-weight: 800;
}

.active-badge span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22C55E;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.16);
}

.loyalty-card,
.qr-card,
.info-card,
.history-card,
.state-card {
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  box-shadow: 0 20px 45px rgba(13, 27, 61, 0.10);
}

.loyalty-card {
  position: relative;
  overflow: hidden;
  padding: 22px;
  background:
    linear-gradient(135deg, rgba(30, 111, 220, 0.82) 0%, rgba(13, 27, 61, 0) 42%),
    linear-gradient(145deg, #0D1B3D 0%, #102655 58%, #0D1B3D 100%);
  color: #FFFFFF;
}

.card-glow {
  position: absolute;
  right: -48px;
  top: -52px;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  background: rgba(34, 197, 94, 0.2);
  filter: blur(2px);
}

.card-top {
  position: relative;
  justify-content: space-between;
  gap: 18px;
}

.card-kicker,
.card-number-block span,
.card-metrics span {
  display: block;
  color: rgba(255, 255, 255, 0.72);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.card-top h1 {
  margin: 8px 0 0;
  font-size: clamp(31px, 10vw, 43px);
  line-height: 1;
  font-weight: 900;
}

.fid-chip {
  flex: 0 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 14px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.12);
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 900;
}

.merchant-name {
  position: relative;
  margin: 13px 0 30px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 15px;
  font-weight: 600;
}

.card-number-block {
  position: relative;
  border-radius: 18px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
}

.card-number-block strong {
  display: block;
  margin-top: 6px;
  color: #FFFFFF;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: clamp(32px, 12vw, 45px);
  line-height: 1;
  letter-spacing: 0;
}

.card-metrics {
  position: relative;
  gap: 12px;
  margin-top: 14px;
}

.card-metrics article {
  min-width: 0;
  flex: 1;
  border-radius: 18px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.card-metrics strong {
  display: block;
  margin-top: 5px;
  font-size: 25px;
  line-height: 1.12;
  font-weight: 900;
}

.progress-block {
  position: relative;
  margin-top: 18px;
}

.progress-track {
  height: 9px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #22C55E 0%, #7DD3FC 100%);
}

.progress-block p {
  margin: 9px 0 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 12px;
  font-weight: 700;
}

.qr-card,
.info-card,
.history-card {
  margin-top: 16px;
  background: #FFFFFF;
}

.qr-card {
  padding: 20px;
  text-align: center;
}

.qr-copy h2 {
  margin: 0;
  color: #0F172A;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 900;
}

.qr-copy p {
  margin: 8px 0 18px;
  color: #64748B;
  font-size: 13px;
  line-height: 1.45;
  font-weight: 600;
}

.qr-frame {
  width: min(100%, 270px);
  min-height: 270px;
  display: grid;
  place-items: center;
  margin: 0 auto;
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  padding: 14px;
  background: #FFFFFF;
  box-shadow: inset 0 0 0 7px #F8FAFC;
}

.qr-frame img {
  display: block;
  width: 100%;
  max-width: 240px;
  aspect-ratio: 1;
}

.qr-frame span {
  color: #64748B;
  font-size: 22px;
  font-weight: 900;
}

.info-card,
.history-card {
  padding: 18px;
}

.section-title {
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.section-title span {
  color: #64748B;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.section-title strong {
  color: #0D1B3D;
  font-size: 15px;
  font-weight: 900;
  text-align: right;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.info-grid article {
  min-width: 0;
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  padding: 13px 11px;
  background: #F8FAFC;
}

.info-grid span {
  display: block;
  color: #64748B;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.info-grid strong {
  display: block;
  margin-top: 5px;
  color: #0F172A;
  font-size: 15px;
  line-height: 1.1;
  font-weight: 900;
}

.info-grid .status-ok {
  color: #15803D;
}

.history-list {
  display: grid;
  gap: 10px;
}

.history-row {
  justify-content: space-between;
  gap: 14px;
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  padding: 13px;
  background: #F8FAFC;
}

.history-row strong {
  display: block;
  color: #0F172A;
  font-size: 14px;
}

.history-row span,
.empty-history {
  color: #64748B;
  font-size: 13px;
}

.history-row b {
  color: #15803D;
  white-space: nowrap;
}

.history-row b.negative {
  color: #0D1B3D;
}

.empty-history {
  margin: 0;
  border: 1px dashed #E2E8F0;
  border-radius: 18px;
  padding: 16px;
  background: #F8FAFC;
  line-height: 1.45;
  font-weight: 600;
}

.public-footer {
  padding: 22px 0 0;
  color: #64748B;
  text-align: center;
  font-size: 12px;
  font-weight: 800;
}

.state-card {
  display: grid;
  justify-items: center;
  gap: 14px;
  margin-top: 24vh;
  padding: 34px 22px;
  background: #FFFFFF;
  text-align: center;
}

.state-card h1,
.state-card p {
  margin: 0;
}

.state-card h1 {
  color: #0D1B3D;
  font-size: 25px;
}

.state-card p {
  color: #64748B;
  font-weight: 600;
}

.state-card.activation {
  border-color: #BFDBFE;
  background: #EFF6FF;
}

.state-card.activation h1 {
  font-size: 34px;
  font-weight: 900;
}

@media (min-width: 900px) {
  .carte-publique-page {
    display: grid;
    align-items: start;
    padding-top: 42px;
  }

  .page-shell {
    width: min(100%, 460px);
  }
}

@media (max-width: 380px) {
  .carte-publique-page {
    padding-inline: 10px;
  }

  .public-header {
    align-items: flex-start;
  }

  .brand span {
    max-width: 160px;
  }

  .loyalty-card,
  .qr-card,
  .info-card,
  .history-card {
    border-radius: 18px;
  }

  .loyalty-card {
    padding: 18px;
  }

  .card-metrics,
  .info-grid {
    grid-template-columns: 1fr;
  }

  .card-metrics {
    display: grid;
  }

  .qr-frame {
    min-height: 244px;
  }

  .qr-frame img {
    max-width: 220px;
  }
}
</style>
